import * as React from 'react'

/**
 * One breakpoint, shared with the stylesheet. Tailwind's `md` is `48rem`, and
 * a rem in a media query follows the browser's font-size setting — so when
 * this hook keyed off `window.innerWidth < 768`, the JS and CSS halves of the
 * nav disagreed for any viewer whose base font size wasn't 16px: at "Large"
 * font in a mid-width window the CSS hid the rail and sidebar while the JS
 * still swore we were on desktop and never mounted the sheet — no navigation
 * surface at all. `matchMedia` evaluates the exact query the CSS does, at the
 * same font size, so the two halves can no longer disagree.
 */
const MOBILE_QUERY = '(width < 48rem)'

/**
 * Trivially true wherever range syntax parses; an engine that cannot parse it
 * returns a list pinned to `matches: false`. The pin is the ONLY signal: a
 * parenthesised query serialises back verbatim through <general-enclosed>,
 * so `.media` never says "not all" for this shape — only input failing the
 * outer media-query grammar does. Exported so the tests can assert the probe
 * really is range syntax, the property the whole detection rests on.
 */
export const RANGE_SYNTAX_PROBE = '(width >= 0px)'

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    // The stylesheet's `md` is range syntax too, so an engine that cannot
    // parse it renders NO desktop nav at any width — the sheet is the only
    // navigation surface left, and "mobile" is the only honest answer.
    if (!window.matchMedia(RANGE_SYNTAX_PROBE).matches) {
      setIsMobile(true)
      return
    }
    const mql = window.matchMedia(MOBILE_QUERY)
    const onChange = () => setIsMobile(mql.matches)
    mql.addEventListener('change', onChange)
    setIsMobile(mql.matches)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  return !!isMobile
}
